import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getConfig,
  getPlayer,
  globalSearch,
} from '@wwe-portal/web/services/client/global';
import {
  QUERY_KEY_GLOBAL_SEARCH,
  QUERY_KEY_CURRENCIES,
  QUERY_KEY_CONFIG,
  QUERY_KEY_PLAYER_PROFILE,
} from '@wwe-portal/web/utils/constants/keys';
import { useMemo } from 'react';
import { staticConfig, useLocalConfig } from '../stores/local-config';

export const useGlobalSearch = (searchValue: string) => {
  const enabled = useMemo(() => !!searchValue, [searchValue]);
  const queryResult = useQuery({
    queryKey: [QUERY_KEY_GLOBAL_SEARCH, searchValue],
    queryFn: () => globalSearch(searchValue),
    enabled,
  });

  return {
    ...queryResult,
    isLoading: queryResult.isLoading || !enabled,
  };
};

export const useInvalidateCurrencies = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_CURRENCIES],
    });
};

const useGetConfig = () => {
  return useQuery({
    queryKey: [QUERY_KEY_CONFIG],
    queryFn: () => getConfig(),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const useCurrentConfig = () => {
  const { data: config, isLoading } = useGetConfig();
  const localConfig = useLocalConfig((state) => state.config);

  return {
    isLoading,
    finalConfig: isLoading
      ? null
      : {
          ...staticConfig,
          ...config,
          ...localConfig,
        },
    originConfig: { ...config },
  };
};

export const useGetPlayerProfile = (name: string) => {
  return useQuery({
    queryKey: [QUERY_KEY_PLAYER_PROFILE, name],
    queryFn: () => getPlayer(name),
    enabled: !!name,
  });
};
